<template>
  <!-- Wrapping v-sheet component for styling purposes -->
  <v-sheet id="quoteform" class="grey lighten-1" dark tag="section" tile>
    <!-- Vertical padding -->
    <div class="py-12"></div>

    <!-- Main container for the form -->
    <v-container>
      <!-- Title for the form -->
      <h2
        class="display-2 font-weight-bold mb-3 text-uppercase text-center primary--text"
      >
        Request a Quote
      </h2>

      <!-- Horizontal divider -->
      <v-responsive class="mx-auto mb-12" width="56">
        <v-divider></v-divider>
        <v-divider></v-divider>
      </v-responsive>

      <!-- The actual quote form -->
      <v-form ref="form" v-model="valid" @submit.prevent="onSubmit">
        <!-- Provide a light theme inside the form -->
        <v-theme-provider light>
          <v-stepper v-model="step" vertical non-linear>
            <!-- ============ STEP 1: Name, surname & school ============ -->
            <v-stepper-step
              step="1"
              editable
              :complete="stepValid(1)"
              :rules="[() => step <= 1 || stepValid(1)]"
            >
              Your details
              <small>Name, surname &amp; who you are</small>
            </v-stepper-step>
            <v-stepper-content step="1">
              <v-text-field
                v-model="section.name"
                label="Name*"
                solo
                :rules="nameRules"
                required
              ></v-text-field>

              <v-text-field
                v-model="section.surname"
                label="Surname*"
                solo
                :rules="surnameRules"
                required
              ></v-text-field>

              <v-select
                v-model="section.role"
                :items="roleOptions"
                label="I am a?*"
                solo
                :rules="roleRules"
                required
                @change="onRoleChange"
              ></v-select>

              <!-- School name is only asked (and only required) for School Management -->
              <v-text-field
                v-if="isSchoolManagement"
                v-model="section.school"
                label="School name*"
                solo
                :rules="schoolRules"
                required
              ></v-text-field>

              <v-btn
                color="primary"
                :disabled="!stepValid(1)"
                @click="step = 2"
              >
                Continue
              </v-btn>
            </v-stepper-content>

            <!-- ============ STEP 2: Email address ============ -->
            <v-stepper-step
              step="2"
              editable
              :complete="stepValid(2)"
              :rules="[() => step <= 2 || stepValid(2)]"
            >
              Email address
              <small>Yours or the school's</small>
            </v-stepper-step>
            <v-stepper-content step="2">
              <v-text-field
                v-model="formfields.email"
                label="Email*"
                solo
                :rules="emailRules"
                required
              ></v-text-field>

              <v-btn
                color="primary"
                :disabled="!stepValid(2)"
                @click="step = 3"
              >
                Continue
              </v-btn>
              <v-btn text @click="step = 1">Back</v-btn>
            </v-stepper-content>

            <!-- ============ STEP 3: Contact number ============ -->
            <v-stepper-step
              step="3"
              editable
              :complete="stepValid(3)"
              :rules="[() => step <= 3 || stepValid(3)]"
            >
              Contact number
              <small>Cell phone or school number</small>
            </v-stepper-step>
            <v-stepper-content step="3">
              <v-text-field
                v-model="section.phone"
                label="Contact number*"
                solo
                type="tel"
                :rules="phoneRules"
                required
              ></v-text-field>

              <v-btn
                color="primary"
                :disabled="!stepValid(3)"
                @click="step = 4"
              >
                Continue
              </v-btn>
              <v-btn text @click="step = 2">Back</v-btn>
            </v-stepper-content>

            <!-- ============ STEP 4: Resources ============ -->
            <v-stepper-step
              step="4"
              editable
              :complete="stepValid(4)"
              :rules="[() => step <= 4 || stepValid(4)]"
            >
              Resources
              <small>Select one or more</small>
            </v-stepper-step>
            <v-stepper-content step="4">
              <v-select
                v-model="section.resources"
                :items="resourceOptions"
                label="Resource(s) requested*"
                solo
                multiple
                chips
                small-chips
                deletable-chips
                :rules="selectRules"
                required
              ></v-select>

              <v-btn
                color="primary"
                :disabled="!stepValid(4)"
                @click="step = 5"
              >
                Continue
              </v-btn>
              <v-btn text @click="step = 3">Back</v-btn>
            </v-stepper-content>

            <!-- ============ STEP 5: Grades ============ -->
            <v-stepper-step
              step="5"
              editable
              :complete="stepValid(5)"
              :rules="[() => step <= 5 || stepValid(5)]"
            >
              Grades
              <small>Select one or more</small>
            </v-stepper-step>
            <v-stepper-content step="5">
              <v-select
                v-model="section.grades"
                :items="gradeOptions"
                label="Grade(s) requested*"
                solo
                multiple
                chips
                small-chips
                deletable-chips
                :rules="selectRules"
                required
              ></v-select>

              <v-btn
                color="primary"
                :disabled="!stepValid(5)"
                @click="step = 6"
              >
                Continue
              </v-btn>
              <v-btn text @click="step = 4">Back</v-btn>
            </v-stepper-content>

            <!-- ============ STEP 6: Subjects ============ -->
            <v-stepper-step
              step="6"
              editable
              :complete="stepValid(6)"
              :rules="[() => step <= 6 || stepValid(6)]"
            >
              Subjects
              <small>Select one or more</small>
            </v-stepper-step>
            <v-stepper-content step="6">
              <v-select
                v-model="section.subjects"
                :items="subjectOptions"
                label="Subject(s) requested*"
                solo
                multiple
                chips
                small-chips
                deletable-chips
                :rules="selectRules"
                required
              ></v-select>

              <v-btn
                color="primary"
                :disabled="!stepValid(6)"
                @click="step = 7"
              >
                Continue
              </v-btn>
              <v-btn text @click="step = 5">Back</v-btn>
            </v-stepper-content>

            <!-- ============ STEP 7: Terms ============ -->
            <v-stepper-step
              step="7"
              editable
              :complete="stepValid(7)"
              :rules="[() => step <= 7 || stepValid(7)]"
            >
              Terms
              <small>Select one or more</small>
            </v-stepper-step>
            <v-stepper-content step="7">
              <v-select
                v-model="section.terms"
                :items="termOptions"
                label="Term(s) requested*"
                solo
                multiple
                chips
                small-chips
                deletable-chips
                :rules="selectRules"
                required
              ></v-select>

              <v-btn
                color="primary"
                :disabled="!stepValid(7)"
                @click="step = 8"
              >
                Continue
              </v-btn>
              <v-btn text @click="step = 6">Back</v-btn>
            </v-stepper-content>

            <!-- ============ STEP 8: LoLT ============ -->
            <v-stepper-step
              step="8"
              editable
              :complete="stepValid(8)"
              :rules="[() => step <= 8 || stepValid(8)]"
            >
              LoLT
              <small>Language of Learning and Teaching</small>
            </v-stepper-step>
            <v-stepper-content step="8">
              <v-select
                v-model="section.lolt"
                :items="loltOptions"
                label="Language of Learning and Teaching*"
                solo
                multiple
                chips
                small-chips
                deletable-chips
                :rules="selectRules"
                required
              ></v-select>

              <v-btn
                color="primary"
                :disabled="!stepValid(8)"
                @click="step = 9"
              >
                Continue
              </v-btn>
              <v-btn text @click="step = 7">Back</v-btn>
            </v-stepper-content>

            <!-- ============ STEP 9: Additional message (optional) ============ -->
            <v-stepper-step step="9" editable :complete="step > 9">
              Additional message
              <small>Optional</small>
            </v-stepper-step>
            <v-stepper-content step="9">
              <v-textarea
                v-model="section.additional"
                label="Anything else we should know?"
                solo
              ></v-textarea>

              <v-col cols="12" class="text-caption pa-0 mb-4">
                This site is protected by reCAPTCHA and the Google
                <a target="_blank" href="https://policies.google.com/privacy">
                  &nbsp;Privacy Policy&nbsp;
                </a>
                and
                <a target="_blank" href="https://policies.google.com/terms">
                  &nbsp;Terms of Service&nbsp;
                </a>
                apply.
              </v-col>

              <!-- Submit button -->
              <v-btn
                :disabled="!allStepsValid || submitted"
                type="submit"
                x-large
                color="primary"
                :loading="submitting"
              >
                {{ submitted ? "Thank You" : "Send request" }}
              </v-btn>
              <v-btn text @click="step = 8">Back</v-btn>

              <!-- Shown while mandatory steps are still outstanding -->
              <div v-if="!allStepsValid" class="text-caption error--text mt-3">
                Complete steps 1 to 8 before sending your request.
              </div>
            </v-stepper-content>
          </v-stepper>
        </v-theme-provider>
      </v-form>
    </v-container>

    <!-- Vertical padding -->
    <div class="py-12"></div>
  </v-sheet>
</template>

<script lang="ts">
import Vue from "vue";
import { API } from "aws-amplify";

export default Vue.extend({
  name: "QuoteForm", // Name of the component
  data: () => ({
    // Data properties
    valid: true, // Form validation status
    step: 1, // Current stepper step
    submitting: false, // Is form currently submitting
    submitted: false, // Has form been submitted
    // Form fields (identical shape to the contact form payload)
    formfields: {
      name: "",
      email: "",
      subject: "",
      message: "",
      recaptchaToken: "",
    },
    // Extra step answers - folded into formfields.message on submit
    section: {
      name: "",
      surname: "",
      role: "", // Step 1 - Teacher / Parent / School Management
      school: "", // Only captured when role is "School Management"
      phone: "",
      resources: [] as string[], // Step 4
      grades: [] as string[], // Step 5
      subjects: [] as string[], // Step 6
      terms: [] as string[], // Step 7
      lolt: [] as string[], // Step 8
      additional: "", // Step 9 (optional)
    },
    // Select options
    roleOptions: ["Teacher", "Parent", "School Management"],
    resourceOptions: [
      "Workbooks",
      "Lesson planning",
      "Assessments",
      "Bundle",
      "Class themes",
      "Posters",
      "PowerPoints",
      "Extra activities",
    ],
    gradeOptions: [
      "Grade R",
      "Grade 1",
      "Grade 2",
      "Grade 3",
      "Grade 4",
      "Grade 5",
      "Grade 6",
      "Grade 7",
      "Grade 8",
      "Grade 9",
      "Grade 10",
      "Grade 12",
    ],
    subjectOptions: [
      "Afrikaans Eerste Addisionele Taal / (AFAL)",
      "Afrikaans Huistaal",
      "English First Additional Language / (EFAL)",
      "English Home Language",
      "Mathematics / Wiskunde",
      "Natural Sciences / Natuurwetenskappe",
      "Geography / Geografie",
      "History / Geskiedenis",
      "PSW / LO / Life Skills",
      "Creative Arts / Skeppende Kunste",
      "EMS / EBW",
    ],
    termOptions: ["Term 1", "Term 2", "Term 3", "Term 4", "All terms"],
    loltOptions: ["Afrikaans", "English"],
    // Validation rules for the name field
    nameRules: [(v: string) => !!v || "Name is required"],
    // Validation rules for the surname field
    surnameRules: [(v: string) => !!v || "Surname is required"],
    // Validation rules for the "I am a?" field
    roleRules: [(v: string) => !!v || "Please select an option"],
    // Validation rules for the school field (only applied for School Management)
    schoolRules: [(v: string) => !!v || "School name is required"],
    // Validation rules for the email field
    emailRules: [
      (v: string) => !!v || "Email is required",
      (v: string) => /.+@.+\..+/.test(v) || "E-mail must be valid",
    ],
    // Validation rules for the contact number field
    phoneRules: [
      (v: string) => !!v || "Contact number is required",
      (v: string) =>
        /^[0-9+()\s-]{10,}$/.test(v) || "Contact number must be valid",
    ],
    // Validation rules for every multi-select field
    selectRules: [
      (v: string[]) => (!!v && v.length > 0) || "Select at least one option",
    ],
  }),
  computed: {
    // School name is only shown and only required for School Management
    isSchoolManagement(): boolean {
      return this.section.role === "School Management";
    },
    // Steps 1 to 8 are mandatory
    allStepsValid(): boolean {
      return [1, 2, 3, 4, 5, 6, 7, 8].every((n) => this.stepValid(n));
    },
  },
  methods: {
    // Clear the school name whenever the user is no longer School Management
    onRoleChange() {
      if (!this.isSchoolManagement) {
        this.section.school = "";
      }
    },
    // Returns whether a given step has been completed correctly
    stepValid(step: number): boolean {
      const s = this.section;
      switch (step) {
        case 1:
          return (
            !!s.name &&
            !!s.surname &&
            !!s.role &&
            (!this.isSchoolManagement || !!s.school)
          );
        case 2:
          return /.+@.+\..+/.test(this.formfields.email);
        case 3:
          return /^[0-9+()\s-]{10,}$/.test(s.phone);
        case 4:
          return s.resources.length > 0;
        case 5:
          return s.grades.length > 0;
        case 6:
          return s.subjects.length > 0;
        case 7:
          return s.terms.length > 0;
        case 8:
          return s.lolt.length > 0;
        default:
          return true; // Step 9 is optional
      }
    },
    // Builds the readable message body sent to the API
    buildMessage(): string {
      const s = this.section;
      const lines = [
        "QUOTE REQUEST",
        "",
        `Contact person: ${s.name} ${s.surname}`,
        `I am a: ${s.role}`,
      ];

      // School name is only captured for School Management
      if (s.school) {
        lines.push(`School: ${s.school}`);
      }

      lines.push(
        `Contact number: ${s.phone}`,
        `Email: ${this.formfields.email}`,
        "",
        `Resource(s) requested: ${s.resources.join(", ")}`,
        `Grade(s) requested: ${s.grades.join(", ")}`,
        `Subject(s) requested: ${s.subjects.join(", ")}`,
        `Term(s) requested: ${s.terms.join(", ")}`,
        `LoLT: ${s.lolt.join(", ")}`
      );

      if (s.additional.trim()) {
        lines.push("", "Additional message:", s.additional.trim());
      }

      return lines.join("\n");
    },
    // Method that handles form submission
    async onSubmit() {
      // Map the step answers onto the existing formfields payload
      this.formfields.name =
        `${this.section.name} ${this.section.surname}`.trim();
      // Use the school name when we have one, otherwise the person's own name
      this.formfields.subject = `Quote request - ${
        this.section.school || this.formfields.name
      }`;
      this.formfields.message = this.buildMessage();

      // Retrieve reCAPTCHA token
      const token = await this.$recaptcha("submit");

      // Assign reCAPTCHA token to formfields
      this.formfields.recaptchaToken = token;
      // Set submitting to true, indicating the form is being submitted
      this.submitting = true;

      // console.log("Form Data:", this.formfields);

      // Send the form data to the API for processing
      await API.post("contactformapi", "/submit", {
        body: this.formfields,
      })
        .then((response) => {
          // Handle successful response from the API
          this.submitting = false; // Set submitting to false, indicating the form has been submitted
          this.submitted = response.success; // Update submitted status based on API response
        })
        .catch((error) => {
          // Log any errors
          console.error(error);
        });
    },
  },
});
</script>
